import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  TextField,
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { 
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase';

const ProviderCommissions = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [commissionRate, setCommissionRate] = useState(20);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [platformSettings, setPlatformSettings] = useState(null);

  // Fetch platform settings for default commission
  const fetchPlatformSettings = async () => {
    try {
      const docRef = doc(db, 'platformSettings', 'main');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setPlatformSettings(docSnap.data());
      }
    } catch (err) {
      console.error('Error fetching platform settings:', err);
    }
  };

  // Fetch service providers
  const fetchProviders = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'users'),
        where('roles.client', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      const providersData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        providersData.push({
          id: doc.id,
          name: data.displayName || data.email || 'Unknown',
          email: data.email,
          commissionRate: data.commissionRate || platformSettings?.defaultCommissionRate || 20,
          approvalStatus: data.approvalStatus || 'pending'
        });
      });
      
      // Only show approved providers
      setProviders(providersData.filter(p => p.approvalStatus === 'approved'));
    } catch (err) {
      console.error('Error fetching providers:', err);
      showSnackbar('Failed to fetch service providers', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchPlatformSettings();
      await fetchProviders();
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle edit commission
  const handleEditCommission = (provider) => {
    setSelectedProvider(provider);
    setCommissionRate(provider.commissionRate);
    setEditDialogOpen(true);
  };

  // Save commission rate
  const handleSaveCommission = async () => {
    if (!selectedProvider) return;

    try {
      const providerRef = doc(db, 'users', selectedProvider.id);
      await updateDoc(providerRef, {
        commissionRate: parseFloat(commissionRate),
        updatedAt: new Date()
      });

      // Update local state
      setProviders(prev => prev.map(p => 
        p.id === selectedProvider.id 
          ? { ...p, commissionRate: parseFloat(commissionRate) }
          : p
      ));

      showSnackbar('Commission rate updated successfully', 'success');
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Error updating commission:', err);
      showSnackbar('Failed to update commission rate', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '', severity: 'success' });
  };

  // Calculate example earnings
  const calculateEarnings = (servicePrice, rate) => {
    const commission = (servicePrice * rate / 100);
    const platformFee = platformSettings?.platformFee || 5;
    const providerEarnings = servicePrice - commission - platformFee;
    return { commission, providerEarnings };
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Service Provider Commission Rates
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Set individual commission rates for each service provider. Default rate: {platformSettings?.defaultCommissionRate || 20}%
      </Typography>

      {loading ? (
        <Typography>Loading service providers...</Typography>
      ) : providers.length === 0 ? (
        <Alert severity="info">No approved service providers found.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Provider Name</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell align="center"><strong>Commission Rate</strong></TableCell>
                <TableCell align="right"><strong>Example ($100 service)</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {providers.map((provider) => {
                const earnings = calculateEarnings(100, provider.commissionRate);
                return (
                  <TableRow key={provider.id}>
                    <TableCell>{provider.name}</TableCell>
                    <TableCell>{provider.email}</TableCell>
                    <TableCell align="center">
                      <Typography 
                        color={provider.commissionRate !== platformSettings?.defaultCommissionRate ? 'primary' : 'text.primary'}
                        fontWeight={provider.commissionRate !== platformSettings?.defaultCommissionRate ? 'bold' : 'normal'}
                      >
                        {provider.commissionRate}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        Commission: -${earnings.commission.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        Earns: ${earnings.providerEarnings.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleEditCommission(provider)}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Commission Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Commission Rate
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Provider:</strong> {selectedProvider?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Default platform rate: {platformSettings?.defaultCommissionRate || 20}%
            </Typography>

            <TextField
              label="Commission Rate"
              type="number"
              value={commissionRate}
              onChange={(e) => setCommissionRate(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText="Set a custom commission rate for this provider"
            />

            {/* Earnings Preview */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Example Calculation ($100 service)
              </Typography>
              <Typography variant="body2">
                Service Price: $100.00
              </Typography>
              <Typography variant="body2">
                Commission ({commissionRate}%): -${((100 * parseFloat(commissionRate || 0)) / 100).toFixed(2)}
              </Typography>
              <Typography variant="body2">
                Platform Fee: -${platformSettings?.platformFee || 5}
              </Typography>
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold', mt: 1 }}>
                Provider Earns: ${calculateEarnings(100, parseFloat(commissionRate || 0)).providerEarnings.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCommission} variant="contained" startIcon={<SaveIcon />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProviderCommissions;
